import { AcademicCapIcon, BuildingLibraryIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, PrinterIcon, UsersIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Flexor, Spinner } from 'shared/src/components';
import { ScreenReaderOnly } from 'shared/src/components/Accessibility';
import { Alert, AlertDescription, AlertTitle, Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/src/components/ui/new';
import { openPdfToPrint } from 'shared/src/utils/downloadDocument';
import { phoneNumberFormatter } from 'shared/src/utils/formatters';
import { getClassDocumentBySessionId, getRequiredClassSessions, getRequiredClasses, signUpForClassSession } from '../fetchers';
import PageEmptyState from './PageEmptyState';
import { renderDataAttribute } from './WorkSchedules';

export default function RequiredClasses() {
  const selectedElectionId = useSelector((state: any) => state.election.electionId);
  const currentUser = useSelector((state: any) => state.currentPollworkerUser);
  const [availableClasses, setAvailableClasses] = useState<any[]>([]);
  const [requiredClasses, setRequiredClasses] = useState<any[]>([]);
  const [showSignupComplete, setShowSignupComplete] = useState(false);
  const [showScheduleView, setShowScheduleView] = useState(false);
  const [hasSignupError, setHasSignupError] = useState<boolean>(false);
  const [signingUp, setSigningUp] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [sessionIdForDownload, setSessionIdForDownload] = useState('');

  useEffect(() => {
    if (!selectedElectionId) return;

    loadRequiredClasses();
  }, [selectedElectionId]);

  async function loadRequiredClasses() {
    setLoading(true);

    return getRequiredClasses(selectedElectionId)
      .then((response) => {
        setRequiredClasses(response);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  async function signUpForClass(sessionId: string) {
    setSigningUp(sessionId);
    setHasSignupError(false);
    setSessionIdForDownload(sessionId);

    signUpForClassSession({
      CustomerId: currentUser.Customer.Id,
      EVUserId: currentUser.EVUserId,
      PWClassSessionId: sessionId,
      PollwokerId: '',
    })
      .then((success) => {
        if (!success) {
          setHasSignupError(true);
          return;
        }
        loadRequiredClasses();

        setHasSignupError(false);
        setShowSignupComplete(true);
        setShowScheduleView(false);
      })
      .finally(() => setSigningUp(undefined));
  }

  function openViewSchedule(sessionId: string) {
    getRequiredClassSessions(selectedElectionId, sessionId).then((response) => {
      setAvailableClasses(response);
      setShowScheduleView(true);
    });
  }

  function downloadSessionDocument(sessionId: string, evUserId: string) {
    getClassDocumentBySessionId(sessionId, evUserId).then(openPdfToPrint);
  }

  return (
    <div className="mt-2">
      <Dialog size="lg" open={showScheduleView} onOpenChange={(open) => !open && setShowScheduleView(false)}>
        <DialogContent className="p-0 gap-0 overflow-hidden">
          <DialogHeader>
            <DialogTitle>Class Schedule</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-700">
              The class list below contains the class session times that are available for the subject matter. Keep in mind that class size is limited, so choose your class session
              as soon as possible.
            </p>
            {hasSignupError ? (
              <Alert className="mt-3" variant="destructive">
                <ExclamationCircleIcon className="size-5" />
                <AlertTitle>Sign up failed</AlertTitle>
                <AlertDescription className="flex items-center justify-between gap-2">
                  <span>There was an error signing up for this class session. Please try again!</span>
                  <Button size="sm" variant="ghost" onClick={() => setHasSignupError(false)}>
                    Close
                  </Button>
                </AlertDescription>
              </Alert>
            ) : null}
            <ol className="divide-y divide-gray-100 pt-3 text-sm leading-6 lg:col-span-7 xl:col-span-8">
              {!availableClasses.length ? (
                <PageEmptyState
                  icon={AcademicCapIcon}
                  emptyMessageHeader="No available class sessions"
                  emptyMessageSubHeader="There are no available sessions yet for this class, be sure to check back soon."
                  reload={loadRequiredClasses}
                />
              ) : (
                availableClasses.map((klass) => {
                  return (
                    <li key={klass.Id} className="relative cursor-pointer flex space-x-6 py-3 hover:bg-gray-50 xl:static">
                      <div className="flex-auto text-gray-900">
                        <Flexor justify="start" className="space-x-3">
                          <InformationCircleIcon className="h-5 w-5" />
                          <h3 className="font-semibold xl:pr-0 space-x-2 flex items-center">
                            <div>{renderDataAttribute(klass.Schedule)}</div>
                          </h3>
                        </Flexor>
                        <dl className="mt-2 flex-col hidden xl:flex-row">
                          <div className="mt-2 flex items-start space-x-3 xl:ml-3.5 xl:mt-0 xl:border-l xl:border-gray-400 xl:border-opacity-50 xl:pl-3.5">
                            <dt className="mt-0.5">
                              <ScreenReaderOnly>Location</ScreenReaderOnly>
                              <BuildingLibraryIcon className="h-5 w-5" aria-hidden="true" />
                            </dt>
                            <dd>{renderDataAttribute(klass.Location)}</dd>
                          </div>
                        </dl>
                        <dl className="mt-2 flex flex-col xl:flex-row">
                          <div className="mt-2 flex items-start space-x-3 xl:ml-3.5 xl:mt-0 xl:border-l xl:border-gray-400 xl:border-opacity-50 xl:pl-3.5">
                            <dt className="mt-0.5" aria-hidden>
                              <UsersIcon className="h-5 w-5" aria-hidden="true" />
                            </dt>
                            <dd>
                              <ScreenReaderOnly>Available Seats</ScreenReaderOnly>
                              {renderDataAttribute(klass.SeatsAvailable)} of {renderDataAttribute(klass.ClassSize)} spots available
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <div>
                          <Button size="sm" disabled={!!signingUp} className="whitespace-nowrap" onClick={() => signUpForClass(klass.Id)}>
                            {signingUp === klass.Id ? <Spinner light /> : null}
                            <span>Sign Up</span>
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ol>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog size="md" open={showSignupComplete} onOpenChange={(open) => !open && setShowSignupComplete(false)}>
        <DialogContent className="p-0 gap-0 overflow-hidden max-w-xl">
          <DialogHeader>
            <DialogTitle>Congratulations!</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Alert variant="success">
              <CheckCircleIcon className="size-5" />
              <AlertTitle>You've been scheduled</AlertTitle>
              <AlertDescription>
                <p>You have been scheduled for this class session.</p>
              </AlertDescription>
            </Alert>
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            <Button variant="ghost" size="sm" data-testid="close-success-modal" aria-label="close" onClick={() => setShowSignupComplete(false)}>
              Close
            </Button>
            <Button size="sm" className="flex items-center space-x-1" onClick={() => downloadSessionDocument(sessionIdForDownload, currentUser.EVUserId)}>
              <PrinterIcon className="h-4 w-4" />
              <span>Print Schedule</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">Required Classes</h1>
          <p className="mt-2 text-sm text-gray-700">
            This list contains the subjects in which you require training. These classes are not optional. Click the View Schedule button to view all of the available times. Be
            aware that class size is limited and you must reserve your seat in class as soon as possible.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="my-20 flex flex-col justify-center items-center">
          <Flexor className="space-x-3">
            <Spinner />
            <span>Loading required classes...</span>
          </Flexor>
        </div>
      ) : (
        <>
          {!requiredClasses.length ? (
            <div className="my-10">
              <PageEmptyState
                icon={AcademicCapIcon}
                emptyMessageHeader="No required classes"
                emptyMessageSubHeader="There are no required classes yet, be sure to check back soon."
                reload={loadRequiredClasses}
              />
            </div>
          ) : (
            <div className="-mx-4 mt-8 sm:-mx-0">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Class
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                      Contact
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                      Phone
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <ScreenReaderOnly>View Schedule</ScreenReaderOnly>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {requiredClasses.map((requiredClass) => {
                    return (
                      <tr key={requiredClass.Id}>
                        <td className="w-auto max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0">{requiredClass.ClassName}</td>
                        <td className="hidden px-3 py-4 text-sm text-gray-800 sm:table-cell">{requiredClass.ContactName}</td>
                        <td className="hidden px-3 py-4 text-sm text-gray-800 sm:table-cell">{phoneNumberFormatter({ value: requiredClass.Phone })}</td>
                        <td className="w-1/2 lg:w-auto">
                          <Button size="sm" className="ml-auto" onClick={() => openViewSchedule(requiredClass.Id)}>
                            View Schedule
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
